import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Loader2, Mail, Lock, User, Building, Users, Briefcase, Eye, EyeOff, FileText, Shield, Bell, Key, Copy, CheckCircle } from "lucide-react";

const MYEUROPACIFIC_LOGO = "https://customer-assets.emergentagent.com/job_65c103ce-390f-4bae-b6d5-2798d8bc6281/artifacts/6in60tne_image.png";
const ROKET_IMAGE = "https://customer-assets.emergentagent.com/job_6e94cf61-ed07-442f-8a8f-1e142db37be8/artifacts/bvtijyq3_IMG_6616.jpg";

const TC_PDF_URL = "https://www.cargo-partner.com/assets/countries/si/general_slovenian_forwarders_terms_and_conditions.pdf";

export default function RegisterPage() {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    full_name: "",
    company_name: "",
    user_type: "customer",  // "europacific" or "customer"
    // Consent checkboxes
    accept_terms: false,
    accept_gdpr: false,
    // Email preferences
    email_service_operations: true,
    email_news_bulletin: false,
    email_newsletter: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [pukCode, setPukCode] = useState("");
  const [pukCopied, setPukCopied] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const copyPuk = () => {
    navigator.clipboard.writeText(pukCode);
    setPukCopied(true);
    toast.success("PUK copied to clipboard!");
    setTimeout(() => setPukCopied(false), 3000);
  };

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { email, password, full_name, company_name, accept_terms, accept_gdpr } = formData;

    if (!email || !password || !full_name || !company_name) {
      toast.error("Please fill in all fields");
      return;
    }

    if (!accept_terms) {
      toast.error("You must accept the Terms & Conditions to continue");
      return;
    }

    if (!accept_gdpr) {
      toast.error("You must accept the GDPR Privacy Policy to continue");
      return;
    }

    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      const result = await register(formData);
      
      if (result?.pending && result?.puk) {
        // Show PUK to user
        setPukCode(result.puk);
        setRegistrationComplete(true);
        toast.success("Account created! Please save your PUK code.");
      } else {
        toast.success("Account created successfully!");
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  // Show PUK screen after successful registration
  if (registrationComplete && pukCode) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center"
        >
          <div className="mb-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800">Registration Complete!</h2>
            <p className="text-gray-600 mt-2">Your account is pending approval.</p>
          </div>

          <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-6 mb-6">
            <div className="flex items-center justify-center gap-2 mb-3">
              <Key className="w-5 h-5 text-amber-600" />
              <span className="font-semibold text-amber-800">Your Personal Unlock Key (PUK)</span>
            </div>
            <div className="bg-white rounded-lg p-4 mb-3">
              <code className="text-3xl font-mono font-bold tracking-widest text-gray-800">
                {pukCode}
              </code>
            </div>
            <Button 
              onClick={copyPuk} 
              variant="outline" 
              className="w-full"
            >
              {pukCopied ? (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-2" />
                  Copy PUK
                </>
              )}
            </Button>
          </div>

          <div className="bg-blue-50 rounded-lg p-4 text-left text-sm text-blue-800 mb-6">
            <p className="font-semibold mb-2">⚠️ Important - Save this PUK!</p>
            <ul className="space-y-1 text-blue-700">
              <li>• Use it to reset your password if you forget it</li>
              <li>• Works even when email is not available</li>
              <li>• Store it somewhere safe (password manager, note)</li>
              <li>• Contact admin if you lose it</li>
            </ul>
          </div>

          <Button 
            onClick={() => navigate("/login")}
            className="w-full"
          >
            Continue to Login
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left - Branding with Roket */}
      <div className="hidden lg:flex bg-gradient-to-br from-[#003366] via-[#004488] to-[#002244] items-center justify-center p-12 relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0">
          <motion.div
            className="absolute top-20 left-10 w-32 h-32 bg-blue-400/10 rounded-full blur-xl"
            animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
            transition={{ duration: 4, repeat: Infinity }}
          />
          <motion.div
            className="absolute bottom-20 right-10 w-48 h-48 bg-cyan-400/10 rounded-full blur-xl"
            animate={{ scale: [1.2, 1, 1.2], opacity: [0.4, 0.6, 0.4] }}
            transition={{ duration: 5, repeat: Infinity }}
          />
        </div>
        
        <div className="text-center relative z-10">
          {/* Roket mascot with animation */}
          <motion.div
            className="relative inline-block mb-8"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            <motion.div
              className="w-36 h-36 rounded-full overflow-hidden border-4 border-amber-400 shadow-2xl mx-auto"
              animate={{ 
                boxShadow: [
                  "0 0 20px rgba(251, 191, 36, 0.3)",
                  "0 0 40px rgba(251, 191, 36, 0.5)",
                  "0 0 20px rgba(251, 191, 36, 0.3)"
                ]
              }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <img 
                src={ROKET_IMAGE} 
                alt="Roket" 
                className="w-full h-full object-cover"
                style={{ objectPosition: 'center 20%' }}
              />
            </motion.div>
            
            {/* Welcome badge */}
            <motion.div
              className="absolute -right-2 -top-1 bg-emerald-500 rounded-full px-2 py-1 shadow-lg"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.5, type: "spring" }}
            >
              <span className="text-white text-xs font-bold">Welcome!</span>
            </motion.div>
          </motion.div>
          
          <motion.h2 
            className="text-3xl font-bold text-white mb-2"
            style={{ fontFamily: "'Segoe UI', system-ui, sans-serif" }}
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <span className="font-light italic">my</span>Europacific
          </motion.h2>
          
          <motion.p 
            className="text-white/60 italic text-sm mb-6"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Logistics simplified
          </motion.p>
          
          <motion.div
            className="bg-white/10 backdrop-blur rounded-xl px-6 py-4 max-w-sm mx-auto"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <p className="text-amber-300 font-semibold text-sm mb-1">Join our community!</p>
            <p className="text-white/80 text-sm">
              Create your account to start tracking shipments and managing your logistics.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Right - Form */}
      <div className="flex items-center justify-center p-8 bg-background">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          {/* Mobile Logo with Roket */}
          <div className="lg:hidden flex flex-col items-center justify-center mb-6">
            <motion.div
              className="w-16 h-16 rounded-full overflow-hidden border-3 border-amber-400 shadow-lg mb-2"
              animate={{ boxShadow: ["0 0 10px rgba(251, 191, 36, 0.3)", "0 0 20px rgba(251, 191, 36, 0.5)", "0 0 10px rgba(251, 191, 36, 0.3)"] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <img 
                src={ROKET_IMAGE} 
                alt="Roket" 
                className="w-full h-full object-cover"
                style={{ objectPosition: 'center 20%' }}
              />
            </motion.div>
            <span className="text-[#003366] text-lg font-light italic">
              my<span className="font-semibold">Europacific</span>
            </span>
          </div>

          <div className="card-surface p-8">
            <div className="mb-8">
              <h1 className="font-heading text-2xl font-bold text-foreground mb-2">
                Create Account
              </h1>
              <p className="text-muted-foreground">
                Start tracking your containers today
              </p>
            </div>

            {/* Important Registration Notice */}
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex gap-3">
                <Shield className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Important Registration Notice</p>
                  <ul className="text-xs text-amber-700 mt-1 space-y-1">
                    <li>• Please register using your <strong>personal name</strong>, not a department or group name</li>
                    <li>• Generic emails (e.g., info@, sales@, office@) will be <strong>automatically declined</strong></li>
                    <li>• Each user must have their own individual account</li>
                  </ul>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Account Type Selection */}
              <div className="space-y-2">
                <Label className="text-foreground">Account Type</Label>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, user_type: "customer" }))}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      formData.user_type === "customer"
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/50"
                    }`}
                    data-testid="user-type-customer"
                  >
                    <Users className={`h-6 w-6 mx-auto mb-2 ${formData.user_type === "customer" ? "text-primary" : "text-muted-foreground"}`} />
                    <p className={`text-sm font-medium ${formData.user_type === "customer" ? "text-primary" : "text-foreground"}`}>
                      Customer
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Track your shipments
                    </p>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, user_type: "europacific" }))}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      formData.user_type === "europacific"
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/50"
                    }`}
                    data-testid="user-type-europacific"
                  >
                    <Briefcase className={`h-6 w-6 mx-auto mb-2 ${formData.user_type === "europacific" ? "text-primary" : "text-muted-foreground"}`} />
                    <p className={`text-sm font-medium ${formData.user_type === "europacific" ? "text-primary" : "text-foreground"}`}>
                      Staff
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Internal team
                    </p>
                  </button>

                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, user_type: "partner" }))}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      formData.user_type === "partner"
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/50"
                    }`}
                    data-testid="user-type-partner"
                  >
                    <FileText className={`h-6 w-6 mx-auto mb-2 ${formData.user_type === "partner" ? "text-primary" : "text-muted-foreground"}`} />
                    <p className={`text-sm font-medium ${formData.user_type === "partner" ? "text-primary" : "text-foreground"}`}>
                      Partner
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Document access
                    </p>
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="full_name" className="text-foreground">
                  Full Name
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="full_name"
                    name="full_name"
                    type="text"
                    placeholder="John Doe"
                    value={formData.full_name}
                    onChange={handleChange}
                    className="input-europacific pl-10"
                    data-testid="register-name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="company_name" className="text-foreground">
                  Company Name
                </Label>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="company_name"
                    name="company_name"
                    type="text"
                    placeholder={formData.user_type === "europacific" ? "Europacific d.o.o." : "Your Company Inc."}
                    value={formData.company_name}
                    onChange={handleChange}
                    className="input-europacific pl-10"
                    data-testid="register-company-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-foreground">
                  Email Address
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder={formData.user_type === "europacific" ? "name@europacific.com" : "you@company.com"}
                    value={formData.email}
                    onChange={handleChange}
                    className="input-europacific pl-10"
                    data-testid="register-email-input"
                  />
                </div>
                {formData.user_type === "europacific" && (
                  <p className="text-xs text-muted-foreground">
                    Use your @europacific.com email for staff access
                  </p>
                )}
              </div>

                <div className="space-y-2">
                <Label htmlFor="password" className="text-foreground">
                  Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Min. 6 characters"
                    value={formData.password}
                    onChange={handleChange}
                    className="input-europacific pl-10 pr-10"
                    data-testid="register-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              {/* Legal Agreements */}
              <div className="space-y-3 pt-4 border-t border-border">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="accept_terms"
                    checked={formData.accept_terms}
                    onChange={(e) => setFormData(prev => ({ ...prev, accept_terms: e.target.checked }))}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                    data-testid="accept-terms-checkbox"
                  />
                  <label htmlFor="accept_terms" className="text-sm text-foreground">
                    <FileText className="inline h-4 w-4 mr-1 text-primary" />
                    I accept the{" "}
                    <a 
                      href={TC_PDF_URL} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:underline font-medium"
                    >
                      Terms & Conditions
                    </a>
                    {" "}(General Conditions of International Freight Forwarders) <span className="text-destructive">*</span>
                  </label>
                </div>

                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="accept_gdpr"
                    checked={formData.accept_gdpr}
                    onChange={(e) => setFormData(prev => ({ ...prev, accept_gdpr: e.target.checked }))}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                    data-testid="accept-gdpr-checkbox"
                  />
                  <label htmlFor="accept_gdpr" className="text-sm text-foreground">
                    <Shield className="inline h-4 w-4 mr-1 text-primary" />
                    I accept the{" "}
                    <a 
                      href="/privacy-policy" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:underline font-medium"
                    >
                      GDPR Privacy Policy
                    </a>
                    {" "}and consent to data processing <span className="text-destructive">*</span>
                  </label>
                </div>
              </div>

              {/* Email Notification Preferences */}
              <div className="space-y-3 pt-4 border-t border-border">
                <div className="flex items-center gap-2 text-foreground font-medium text-sm">
                  <Bell className="h-4 w-4 text-primary" />
                  Email Notification Preferences
                </div>
                <p className="text-xs text-muted-foreground">Select which emails you'd like to receive from us:</p>

                <div className="space-y-2 ml-1">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="email_service"
                      checked={formData.email_service_operations}
                      onChange={(e) => setFormData(prev => ({ ...prev, email_service_operations: e.target.checked }))}
                      className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                      data-testid="email-service-checkbox"
                    />
                    <label htmlFor="email_service" className="text-sm text-foreground">
                      <span className="font-medium">Service & Operations</span>
                      <span className="text-muted-foreground"> - Shipment updates, tracking alerts, invoices</span>
                    </label>
                  </div>

                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="email_news"
                      checked={formData.email_news_bulletin}
                      onChange={(e) => setFormData(prev => ({ ...prev, email_news_bulletin: e.target.checked }))}
                      className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                      data-testid="email-news-checkbox"
                    />
                    <label htmlFor="email_news" className="text-sm text-foreground">
                      <span className="font-medium">News Bulletin</span>
                      <span className="text-muted-foreground"> - Industry news, port updates, schedule changes</span>
                    </label>
                  </div>

                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="email_newsletter"
                      checked={formData.email_newsletter}
                      onChange={(e) => setFormData(prev => ({ ...prev, email_newsletter: e.target.checked }))}
                      className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                      data-testid="email-newsletter-checkbox"
                    />
                    <label htmlFor="email_newsletter" className="text-sm text-foreground">
                      <span className="font-medium">Newsletter</span>
                      <span className="text-muted-foreground"> - Company news, promotions, events</span>
                    </label>
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                className="btn-primary w-full h-12"
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  "Create Account"
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-muted-foreground">
                Already have an account?{" "}
                <Link
                  to="/login"
                  className="text-primary hover:underline font-medium"
                  data-testid="login-link"
                >
                  Sign In
                </Link>
              </p>
            </div>
          </div>

          <div className="mt-6 text-center">
            <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">
              ← Back to Home
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export { RegisterPage };
